import { useEffect, useRef, useState } from 'react'

import { decodeSvgBuffer } from '../../../shared/utils/svgUtils.js'
import { getFloorPlanUrl, getDefsUrl } from '../../../shared/utils/unitAssets.js'
import { Logger } from '../../../shared/utils/logger.js'
import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js'

import { applyStandardSvgModifiers, readSvgDimensions } from '../utils/svgPipeline.js'

// ── Module-level cache ────────────────────────────────────────────────────────
//
// Lives outside the hook so it survives component unmounts. When the user
// closes ProductView and re-opens the same product, we skip the network round-trip.
//
// Key format: `${projectDataUrl}|${uuid}|${refTier}`
// Entry shape: { svgElement: SVGElement, svgDimensions: { width: number, height: number } }
//
// Max size intentionally small (50) — floor-plan SVGs are large DOM trees and memory matters.

const MAX_CACHE_SIZE = 50

/** @type {Map<string, { svgElement: SVGElement, svgDimensions: { width: number, height: number } }>} */
const planSvgCache = new Map()

// ── Cache helpers ─────────────────────────────────────────────────────────────

function buildCacheKey(projectDataUrl, uuid, refTier) {
  return `${projectDataUrl}|${uuid}|${refTier}`
}

function pruneCache() {
  if (planSvgCache.size >= MAX_CACHE_SIZE) {
    const firstKey = planSvgCache.keys().next().value
    planSvgCache.delete(firstKey)
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * useProductSvg — fetches, decodes, modifies, and caches a product floor-plan SVG.
 *
 * Pipeline:
 *   1. Build URL from product.uuid + product.id_cad + refTier
 *   2. Check module-level cache (survives unmount)
 *   3. If miss: fetch → detect encoding → decode → DOMParse → applyStandardSvgModifiers → cache
 *   4. Return clone of cached SVGElement (each call site gets an independent DOM tree)
 *
 * The returned `svgElement` is a **clone** — consumers may mutate it freely
 * (mode-specific modifications, event listeners) without corrupting the cache.
 *
 * Level 2 hook: manages async state.
 *
 * @param {{ product: object|null, refTier: number|null, projectDataUrl: string|null }} params
 * @returns {{ svgElement: SVGElement|null, svgDimensions: { width: number, height: number }, status: string, error: string|null }}
 */
export function useProductSvg({ product, refTier, projectDataUrl }) {
  const [svgElement, setSvgElement]     = useState(null)
  const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 })
  const [status, setStatus]             = useState(FETCH_STATUS.LOADING)
  const [error, setError]               = useState(null)

  // Track the request we're currently waiting for so that stale responses
  // from a previous product/tier are discarded.
  const abortRef = useRef(null)

  useEffect(() => {
    // Guard: all three inputs required
    if (!product?.uuid || !product?.id_cad || refTier == null || !projectDataUrl) {
      setStatus(FETCH_STATUS.LOADING) // still waiting for data
      setSvgElement(null)
      return
    }

    const cacheKey = buildCacheKey(projectDataUrl, product.uuid, refTier)

    // ── Cache hit ──────────────────────────────────────────────────────────
    if (planSvgCache.has(cacheKey)) {
      const { svgElement: cached, svgDimensions: dims } = planSvgCache.get(cacheKey)
      setSvgElement(cached.cloneNode(true))
      setSvgDimensions(dims)
      setStatus(FETCH_STATUS.READY)
      setError(null)
      return
    }

    // ── Fetch ──────────────────────────────────────────────────────────────
    const controller = new AbortController()
    abortRef.current = controller
    setStatus(FETCH_STATUS.LOADING)
    setSvgElement(null)
    setError(null)

    const svgUrl = getFloorPlanUrl(projectDataUrl, product, refTier, 'svg')
    const pngUrl = getFloorPlanUrl(projectDataUrl, product, refTier, '3d')
    const defsUrl = getDefsUrl(projectDataUrl)

    ;(async () => {
      try {
        const response = await fetch(svgUrl, { signal: controller.signal })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const buffer  = await response.arrayBuffer()
        const svgText = decodeSvgBuffer(buffer)

        if (!svgText.trimStart().startsWith('<svg') && !svgText.trimStart().startsWith('<?xml')) {
          throw new Error('Response is not valid SVG markup')
        }

        const parser = new DOMParser()
        const doc    = parser.parseFromString(svgText, 'image/svg+xml')

        const parseError = doc.querySelector('parsererror')
        if (parseError) {
          throw new Error(`SVG parse error: ${parseError.textContent.slice(0, 200)}`)
        }

        const svgEl = doc.querySelector('svg')
        if (!svgEl) {
          throw new Error('No <svg> root element found in response')
        }

        // Apply one-time DOM mutations to the canonical base element
        applyStandardSvgModifiers(svgEl, defsUrl, pngUrl)

        const dims = readSvgDimensions(svgEl)

        pruneCache()
        planSvgCache.set(cacheKey, { svgElement: svgEl, svgDimensions: dims })

        // Return a clone so the cache entry stays pristine
        setSvgElement(svgEl.cloneNode(true))
        setSvgDimensions(dims)
        setStatus(FETCH_STATUS.READY)
        setError(null)
      } catch (err) {
        if (err.name === 'AbortError') return // stale fetch — ignore

        Logger.error({ message: 'useProductSvg: fetch failed', content: { url: svgUrl, error: err.message } })
        setSvgElement(null)
        setStatus(FETCH_STATUS.ERROR)
        setError(err.message ?? 'Unknown error loading floor plan')
      }
    })()

    return () => {
      controller.abort()
    }
  }, [product?.uuid, product?.id_cad, refTier, projectDataUrl])

  return { svgElement, svgDimensions, status, error }
}
